import { useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { X, ExternalLink, Printer, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { useReadOnly } from "../../access/ReadOnly";
import { buildLocationLabel, browserPrint } from "../helpers/printAgent";
import LaneStatusPill from "./LaneStatusPill";
import { STATUS_SLUGS, type StatusSlug } from "./laneStatus";
import { statusLabel } from "./laneStatusConfig";
import { laneStatusLocked, type Lane } from "./useLanes";

interface LaneDetailDrawerProps {
    lane: Lane | null;
    onClose: () => void;
    /** Manually change this lane's status (only fired when unlocked). */
    onStatusChange?: (barcode: string, status: StatusSlug) => void;
}

// Demo auction/tote data derived from the lane barcode so the drawer
// stays in sync with the page-level mock. When the BE feed lands, this
// component will be passed real auctions/totes via props instead.
const seededRand = (seed: string, salt: number): number => {
    let h = 5381 ^ salt;
    for (let i = 0; i < seed.length; i++) {
        h = ((h << 5) + h + seed.charCodeAt(i)) >>> 0;
    }
    return h;
};

const mockAuctions = (lane: Lane): Array<{ id: string; title: string; lots: number; end: string | null }> => {
    if (lane.auctions === 0) return [];
    return Array.from({ length: lane.auctions }, (_, i) => {
        const r = seededRand(lane.barcode, i + 1);
        const id = `AUC-${(r % 9000 + 1000).toString()}`;
        const lots = (r >> 5) % 18 + 3;
        const titles = ["Spring Clearance", "Showroom Returns", "Premium Lot", "Restock Wave", "Vendor Bundle"];
        return {
            id,
            title: titles[(r >> 11) % titles.length],
            lots,
            end: lane.auction_end_date,
        };
    });
};

const mockTotes = (lane: Lane): Array<{ id: string; items: number; arrived: string }> => {
    if (lane.totes === 0) return [];
    return Array.from({ length: lane.totes }, (_, i) => {
        const r = seededRand(lane.barcode, 1000 + i);
        const hh = (r % 12 + 8).toString().padStart(2, "0");
        const mm = ((r >> 4) % 60).toString().padStart(2, "0");
        return {
            id: `T${(r % 9000 + 1000).toString()}`,
            items: (r >> 7) % 24 + 4,
            arrived: `${hh}:${mm}`,
        };
    });
};

const formatDate = (iso: string | null): string => {
    if (!iso) return "—";
    const d = new Date(iso + "T00:00:00");
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    return `${dd}/${mm}`;
};

const LaneDetailDrawer = ({ lane, onClose, onStatusChange }: LaneDetailDrawerProps) => {
    const { t, language } = useTranslation();
    const readOnly = useReadOnly();
    const navigate = useNavigate();
    const open = lane !== null;

    // Status options, A-Z by label in the active language (matches the
    // ordering in the Lanes settings editor).
    const statusOptions = useMemo(
        () => [...STATUS_SLUGS].sort((a, b) => statusLabel(a, language).localeCompare(statusLabel(b, language))),
        [language],
    );

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    const auctions = useMemo(() => (lane ? mockAuctions(lane) : []), [lane]);
    const totes = useMemo(() => (lane ? mockTotes(lane) : []), [lane]);

    const handlePrint = async () => {
        if (!lane) return;
        const html = buildLocationLabel(lane.barcode, {
            warehouse: lane.warehouse,
            displayWord: lane.zone ? `Lane ${lane.zone}` : "Lane",
            size: "a5",
        });
        const r = await browserPrint(html);
        if (!r.success) toast.error(r.error || t("server_error"));
    };

    const handleOpenLocations = () => {
        if (!lane) return;
        navigate(`/admin/warehouse/locations?search=${encodeURIComponent(lane.barcode)}`);
    };

    // Portalled to document.body so the drawer escapes any ancestor
    // stacking context (the page container's overflow/height inline
    // styles can trap a fixed-position child below the global topbar).
    return createPortal(
        <>
            <div
                className={`lanes-drawer-scrim${open ? " lanes-drawer-scrim--open" : ""}`}
                onClick={onClose}
                aria-hidden="true"
            />
            <aside
                className={`lanes-drawer${open ? " lanes-drawer--open" : ""}`}
                role="dialog"
                aria-modal="true"
                aria-hidden={!open}
            >
                {lane && (
                    <>
                        {/* div, not <header>: the app's global CSS has an
                            element selector `header { background: ...; height: 56px }`
                            that would otherwise style this band like the topbar. */}
                        <div className="lanes-drawer-header">
                            <div>
                                <div className="lanes-drawer-eyebrow">
                                    {lane.warehouse}{lane.zone ? ` · ${lane.zone}` : ""}
                                </div>
                                <h2 className="lanes-drawer-title">Lane {lane.barcode}</h2>
                            </div>
                            <button
                                type="button"
                                className="lanes-drawer-close"
                                onClick={onClose}
                                aria-label={t("lanes_drawer_close")}
                            >
                                <X size={16} strokeWidth={2} />
                            </button>
                        </div>

                        <div className="lanes-drawer-meta">
                            {laneStatusLocked(lane) ? (
                                <>
                                    <LaneStatusPill status={lane.status} size="lg" />
                                    <span className="lanes-drawer-status-lock" title={t("lanes_status_locked_hint")}>
                                        <Lock size={12} strokeWidth={2} />
                                        {t("lanes_status_locked")}
                                    </span>
                                </>
                            ) : (
                                <label className="lanes-drawer-status-edit">
                                    <span className="lanes-drawer-status-edit-label">{t("lanes_col_status")}</span>
                                    <select
                                        className="lanes-drawer-status-select"
                                        value={lane.status}
                                        onChange={e => onStatusChange?.(lane.barcode, e.target.value as StatusSlug)}
                                        disabled={readOnly}
                                        aria-disabled={readOnly}
                                        title={readOnly ? t("read_only_mode") : undefined}
                                    >
                                        {statusOptions.map(slug => (
                                            <option key={slug} value={slug}>{statusLabel(slug, language)}</option>
                                        ))}
                                    </select>
                                </label>
                            )}
                            {lane.auction_end_date && (
                                <span className="lanes-drawer-deadline-chip">
                                    {t("lanes_drawer_closes_prefix")} {formatDate(lane.auction_end_date)}
                                </span>
                            )}
                        </div>

                        <div className="lanes-drawer-stats">
                            <div className="lanes-drawer-stat">
                                <div className="lanes-drawer-stat-label">{t("lanes_drawer_stat_totes")}</div>
                                <div className="lanes-drawer-stat-value">{lane.totes}</div>
                            </div>
                            <div className="lanes-drawer-stat">
                                <div className="lanes-drawer-stat-label">{t("lanes_drawer_stat_auctions")}</div>
                                <div className="lanes-drawer-stat-value">{lane.auctions}</div>
                            </div>
                            <div className="lanes-drawer-stat">
                                <div className="lanes-drawer-stat-label">{t("lanes_drawer_stat_walk")}</div>
                                <div className="lanes-drawer-stat-value">{lane.walk_order ?? "—"}</div>
                            </div>
                        </div>

                        <div className="lanes-drawer-body">
                            <section>
                                <div className="lanes-drawer-section-header">
                                    <span className="lanes-drawer-section-title">{t("lanes_drawer_active_auctions")}</span>
                                    <span className="lanes-drawer-section-count">{auctions.length}</span>
                                </div>
                                {auctions.length === 0 ? (
                                    <div className="lanes-drawer-empty">{t("lanes_drawer_no_auctions")}</div>
                                ) : (
                                    auctions.map(a => (
                                        <div key={a.id} className="lanes-drawer-auction-row">
                                            <div className="lanes-drawer-auction-meta">
                                                <div className="lanes-drawer-auction-title">{a.title}</div>
                                                <div className="lanes-drawer-auction-sub">
                                                    {a.id} · {a.lots} {t("lanes_drawer_lots_suffix")}
                                                </div>
                                            </div>
                                            <div className="lanes-drawer-auction-sub" style={{ fontVariantNumeric: "tabular-nums" }}>
                                                {formatDate(a.end)}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </section>

                            <section>
                                <div className="lanes-drawer-section-header">
                                    <span className="lanes-drawer-section-title">{t("lanes_drawer_totes_on_lane")}</span>
                                    <span className="lanes-drawer-section-count">{totes.length}</span>
                                </div>
                                {totes.length === 0 ? (
                                    <div className="lanes-drawer-empty">{t("lanes_drawer_no_totes")}</div>
                                ) : (
                                    <div className="lanes-drawer-totes-grid">
                                        {totes.map(t2 => (
                                            <div key={t2.id} className="lanes-drawer-tote-card">
                                                <div className="lanes-drawer-tote-id">{t2.id}</div>
                                                <div className="lanes-drawer-tote-sub">
                                                    {t2.items} items · {t2.arrived}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </section>
                        </div>

                        <div className="lanes-drawer-footer">
                            <button
                                type="button"
                                className="lanes-drawer-btn-ghost"
                                onClick={handlePrint}
                            >
                                <Printer size={14} strokeWidth={1.8} style={{ marginRight: 6, verticalAlign: -2 }} />
                                {t("lanes_drawer_print")}
                            </button>
                            <button
                                type="button"
                                className="lanes-drawer-btn-primary"
                                onClick={handleOpenLocations}
                            >
                                <ExternalLink size={14} strokeWidth={1.8} style={{ marginRight: 6, verticalAlign: -2 }} />
                                {t("lanes_drawer_open_in_locations")}
                            </button>
                        </div>
                    </>
                )}
            </aside>
        </>,
        document.body,
    );
};

export default LaneDetailDrawer;
